import { AloftPrice, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectMarketAdjustedSalePrice,
  selectMarketAdjustedSalePrices,
} from './calculatedMarketAdjustmentSelectors/selectMarketAdjustedSalePrice.ts';
import { selectCompSequence } from './orderSelectors.ts';
import { selectImprovementsCost, selectImprovementsCosts } from './selectImprovementsCost.ts';
import { selectSubjectValue } from './selectSubjectValue.ts';

const calculateImprovementsValueRatio = (
  improvementsCost?: ReturnType<typeof selectImprovementsCost>,
  marketAdjustedSalePrice?: ReturnType<typeof selectMarketAdjustedSalePrice>,
) => {
  if (isUndefined(improvementsCost) || isUndefined(marketAdjustedSalePrice)) {
    return undefined;
  }

  if (improvementsCost.equals(AloftPrice.zero)) {
    return 0;
  }

  return improvementsCost.ratio(marketAdjustedSalePrice);
};

function formatImprovementsValueRatio(improvementsValueRatio: number | undefined) {
  if (isUndefined(improvementsValueRatio) || improvementsValueRatio === Infinity) {
    return undefined;
  }

  return `${Math.round(improvementsValueRatio * 100)}%`;
}

const selectImprovementsValueRatio = createSelector(
  [selectImprovementsCost, selectMarketAdjustedSalePrice],
  calculateImprovementsValueRatio,
);

const selectSubjectImprovementsValueRatio = createSelector(
  [selectImprovementsCost, selectSubjectValue],
  calculateImprovementsValueRatio,
);

export const selectSubjectImprovementsValueRatioDisplay = createSelector(
  [selectSubjectImprovementsValueRatio],
  (subjectImprovementsValueRatio) => formatImprovementsValueRatio(subjectImprovementsValueRatio),
);

export const selectSiteImprovementsRatios = createSelector(
  [selectImprovementsCosts, selectMarketAdjustedSalePrices, selectCompSequence],
  (improvementsCosts, marketAdjustedSalesPrices, compSequence) => {
    const properties = ['subject', ...compSequence];

    return Object.fromEntries(
      properties.map((propID) => [
        propID,
        calculateImprovementsValueRatio(
          improvementsCosts[propID],
          marketAdjustedSalesPrices[propID],
        ),
      ]),
    );
  },
);

export const selectImprovementsValueRatioDisplay = createSelector(
  [selectImprovementsValueRatio],
  (improvementsValueRatio) => formatImprovementsValueRatio(improvementsValueRatio),
);
